/**
 * Composer 작성폼의 개선 이미지 AI 호스트 콜백 — 작성 성격의 web 페이지가 공유한다
 * (`useComposerSuggestHandlers` 동형). companyId가 없으면 undefined로 두어 **미배선**(rc 위젯이
 * 표시 전용으로 degrade — 조직 컨텍스트 없이 인증 API를 때리지 않는다). 공개/외부(external
 * 무인증) 경로는 이 훅을 쓰지 않는다.
 *
 * 취소: 호스트는 동시 생성 상한(세마포어)을 갖고, 이 훅이 마운트 동안 소유한
 * AbortController의 신호를 매 호출에 건네준다. 화면이 떠나면(훅 언마운트·companyId 교체) 진행 중
 * 요청은 axios가 끊고 대기 중 호출은 호출 없이 reject한다 — rc 위젯의 `isAlive`는 커밋만 버리고
 * 요청 자체는 살려 두었기 때문에 비용이 그대로 나갔다. 행 삭제 단위의 취소는 rc 계약(`generate`
 * opts)에 signal이 없어 여기서는 다루지 않는다.
 * AbortController를 effect 안에서 만드는 이유: StrictMode 개발 재마운트가 cleanup을 한 번 돌리므로
 * memo에 닫아 넣으면 되살아난 화면의 신호가 이미 끊긴 채가 된다.
 */
import { useEffect, useMemo, useRef } from 'react';
import type { ComposerImprovementImageHost } from '@safetyhub/report-composer';
import { createComposerImprovementImageHost } from '../lib/composerImprovementImage';

export function useComposerImprovementImageHost(
  companyId: string | undefined,
): ComposerImprovementImageHost | undefined {
  const controllerRef = useRef<AbortController | null>(null);
  const host = useMemo(
    () =>
      companyId
        ? createComposerImprovementImageHost(companyId, {
            getSignal: () => controllerRef.current?.signal,
          })
        : undefined,
    [companyId],
  );
  useEffect(() => {
    if (!host) return;
    const controller = new AbortController();
    controllerRef.current = controller;
    return () => {
      controller.abort();
      if (controllerRef.current === controller) controllerRef.current = null;
    };
  }, [host]);
  return host;
}
